import { useEffect } from "react";

interface SeoHeadProps {
  title: string;
  description: string;
  path?: string;
  image?: string;
  imageWidth?: number;
  imageHeight?: number;
  imageAlt?: string;
  /** Imagem específica para Twitter Card (1200×628). Default: /twitter-card-1200x628.png */
  twitterImage?: string;
  keywords?: string;
  type?: "website" | "article" | "profile";
  noindex?: boolean;
  lang?: string;
  locale?: string;
  /** ISO 8601 date string — only used when type="article" */
  publishedTime?: string;
  /** ISO 8601 date string — only used when type="article" */
  modifiedTime?: string;
  /** Article author name — only used when type="article" */
  author?: string;
  alternates?: Array<{
    hreflang: string;
    path?: string;
  }>;
  structuredData?: Record<string, unknown> | Record<string, unknown>[];
}

const SITE_NAME = "Qlivar";
const DEFAULT_IMAGE = "/og-cover-1200x630.png";
const DEFAULT_TWITTER_IMAGE = "/twitter-card-1200x628.png";
const SITE_URL = (import.meta.env.VITE_SITE_URL || "https://qlivar.com").replace(/\/$/, "");
const JSON_LD_SCRIPT_ID = "qlivar-seo-ld-json";
const ALTERNATE_LINK_SELECTOR = 'link[data-qlivar-seo="alternate"]';
const OG_ALTERNATE_SELECTOR = 'meta[data-qlivar-seo="og-locale-alt"]';

const DEFAULT_ALTERNATES = [
  "x-default",
  "en-US",
  "pt-BR",
] as const;

const absoluteUrl = (path?: string) => {
  if (!path) return SITE_URL;
  if (path.startsWith("http://") || path.startsWith("https://")) return path;
  return `${SITE_URL}${path.startsWith("/") ? path : `/${path}`}`;
};

const setMetaTag = (selector: string, attrs: Record<string, string>) => {
  let tag = document.head.querySelector(selector) as HTMLMetaElement | null;

  if (!tag) {
    tag = document.createElement("meta");
    document.head.appendChild(tag);
  }

  Object.entries(attrs).forEach(([key, value]) => {
    tag!.setAttribute(key, value);
  });
};

const toOgLocale = (hreflang: string) => {
  if (hreflang === "x-default") return null;
  const [language, region] = hreflang.split("-");
  if (!language || !region) return null;
  return `${language.toLowerCase()}_${region.toUpperCase()}`;
};

export const SeoHead = ({
  title,
  description,
  path = "/",
  image = DEFAULT_IMAGE,
  imageWidth = 1200,
  imageHeight = 630,
  imageAlt,
  twitterImage = DEFAULT_TWITTER_IMAGE,
  keywords,
  type = "website",
  noindex = false,
  lang,
  locale,
  publishedTime,
  modifiedTime,
  author,
  alternates,
  structuredData,
}: SeoHeadProps) => {
  const currentDocumentLang = document.documentElement.lang.toLowerCase();
  const resolvedLang = lang || (currentDocumentLang.startsWith("pt") ? "pt" : "en");
  const resolvedLocale = locale || (resolvedLang === "pt" ? "pt_BR" : "en_US");

  useEffect(() => {
    const fullTitle = title.includes(SITE_NAME) ? title : `${title} | ${SITE_NAME}`;
    const canonicalUrl = absoluteUrl(path);
    const imageUrl = absoluteUrl(image);

    document.title = fullTitle;
    document.documentElement.lang = resolvedLang;

    setMetaTag('meta[name="description"]', {
      name: "description",
      content: description,
    });

    if (keywords) {
      setMetaTag('meta[name="keywords"]', {
        name: "keywords",
        content: keywords,
      });
    }

    setMetaTag('meta[name="robots"]', {
      name: "robots",
      content: noindex ? "noindex, nofollow" : "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1",
    });

    setMetaTag('meta[property="og:type"]', {
      property: "og:type",
      content: type,
    });

    setMetaTag('meta[property="og:site_name"]', {
      property: "og:site_name",
      content: SITE_NAME,
    });

    setMetaTag('meta[property="og:locale"]', {
      property: "og:locale",
      content: resolvedLocale,
    });

    setMetaTag('meta[property="og:url"]', {
      property: "og:url",
      content: canonicalUrl,
    });

    setMetaTag('meta[property="og:title"]', {
      property: "og:title",
      content: fullTitle,
    });

    setMetaTag('meta[property="og:description"]', {
      property: "og:description",
      content: description,
    });

    setMetaTag('meta[property="og:image"]', {
      property: "og:image",
      content: imageUrl,
    });

    setMetaTag('meta[property="og:image:width"]', {
      property: "og:image:width",
      content: String(imageWidth),
    });

    setMetaTag('meta[property="og:image:height"]', {
      property: "og:image:height",
      content: String(imageHeight),
    });

    if (imageAlt || description) {
      setMetaTag('meta[property="og:image:alt"]', {
        property: "og:image:alt",
        content: imageAlt || description.slice(0, 120),
      });
    }

    setMetaTag('meta[name="twitter:card"]', {
      name: "twitter:card",
      content: "summary_large_image",
    });

    setMetaTag('meta[name="twitter:site"]', {
      name: "twitter:site",
      content: "@qlivar",
    });

    setMetaTag('meta[name="twitter:creator"]', {
      name: "twitter:creator",
      content: "@qlivar",
    });

    setMetaTag('meta[name="twitter:title"]', {
      name: "twitter:title",
      content: fullTitle,
    });

    setMetaTag('meta[name="twitter:description"]', {
      name: "twitter:description",
      content: description,
    });

    const twitterImageUrl = absoluteUrl(twitterImage);

    setMetaTag('meta[name="twitter:image"]', {
      name: "twitter:image",
      content: twitterImageUrl,
    });

    setMetaTag('meta[name="twitter:image:alt"]', {
      name: "twitter:image:alt",
      content: imageAlt || description.slice(0, 120),
    });

    // Article-specific Open Graph tags
    ['article:published_time', 'article:modified_time', 'article:author'].forEach((prop) => {
      document.head.querySelector(`meta[property="${prop}"]`)?.remove();
    });
    if (type === 'article') {
      if (publishedTime) {
        setMetaTag('meta[property="article:published_time"]', {
          property: 'article:published_time',
          content: publishedTime,
        });
      }
      if (modifiedTime) {
        setMetaTag('meta[property="article:modified_time"]', {
          property: 'article:modified_time',
          content: modifiedTime,
        });
      }
      if (author) {
        setMetaTag('meta[property="article:author"]', {
          property: 'article:author',
          content: author,
        });
      }
    }

    let canonicalTag = document.head.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonicalTag) {
      canonicalTag = document.createElement("link");
      canonicalTag.rel = "canonical";
      document.head.appendChild(canonicalTag);
    }
    canonicalTag.href = canonicalUrl;

    document.head.querySelectorAll(ALTERNATE_LINK_SELECTOR).forEach((tag) => tag.remove());

    const alternateEntries = (alternates && alternates.length > 0)
      ? alternates
      : DEFAULT_ALTERNATES.map((hreflang) => ({ hreflang, path }));

    alternateEntries.forEach((alternate) => {
      const altLink = document.createElement("link");
      altLink.rel = "alternate";
      altLink.hreflang = alternate.hreflang;
      altLink.href = absoluteUrl(alternate.path || path);
      altLink.setAttribute("data-qlivar-seo", "alternate");
      document.head.appendChild(altLink);
    });

    document.head.querySelectorAll(OG_ALTERNATE_SELECTOR).forEach((tag) => tag.remove());

    const alternateOgLocales = Array.from(
      new Set(
        alternateEntries
          .map((item) => toOgLocale(item.hreflang))
          .filter((item): item is string => Boolean(item) && item !== resolvedLocale)
      )
    );

    alternateOgLocales.forEach((altLocale) => {
      const meta = document.createElement("meta");
      meta.setAttribute("property", "og:locale:alternate");
      meta.setAttribute("content", altLocale);
      meta.setAttribute("data-qlivar-seo", "og-locale-alt");
      document.head.appendChild(meta);
    });

    const existingJsonLd = document.getElementById(JSON_LD_SCRIPT_ID);
    if (structuredData) {
      const jsonLdScript = existingJsonLd || document.createElement("script");
      jsonLdScript.id = JSON_LD_SCRIPT_ID;
      jsonLdScript.setAttribute("type", "application/ld+json");
      jsonLdScript.textContent = JSON.stringify(structuredData);

      if (!existingJsonLd) {
        document.head.appendChild(jsonLdScript);
      }
    } else if (existingJsonLd) {
      existingJsonLd.remove();
    }
  }, [alternates, author, description, image, keywords, modifiedTime, noindex, path, publishedTime, resolvedLang, resolvedLocale, structuredData, title, type]);

  return null;
};
